import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail, Shield } from "lucide-react";
import { apiLogin, apiMe, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Admin login. Same backend endpoint as user login (/api/auth/login),
 * but on success we verify role==="admin" before granting access.
 * Non-admin emails get a generic "invalid credentials" message so we
 * don't leak that admin accounts exist.
 */
export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const { refresh } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await apiLogin({ email, password });
      const me = await apiMe();
      if (me.role !== "admin") {
        setErr("Invalid credentials");
        setBusy(false);
        return;
      }
      await refresh();
      navigate("/admin", { replace: true });
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <section className="pt-16 pb-20 max-w-md mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="border border-neutral-900 bg-neutral-950/60 p-7"
      >
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-[#FFB000] mb-3">
          <Shield className="w-3.5 h-3.5" /> ── restricted ──
        </div>
        <h1 className="text-3xl font-black tracking-tight text-white">Admin access</h1>
        <p className="text-sm text-neutral-400 mt-1.5 mb-6">
          Authorized personnel only.
        </p>

        <form onSubmit={submit} className="space-y-3" data-testid="admin-login-form">
          <label className="flex items-center gap-2 border border-neutral-800 bg-neutral-950 px-3.5 focus-within:border-neutral-600 transition-colors">
            <Mail className="w-4 h-4 text-neutral-500" />
            <input
              data-testid="admin-email"
              type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email" autoComplete="username"
              className="flex-1 bg-transparent border-0 outline-none py-3 text-sm text-white placeholder:text-neutral-600"
            />
          </label>
          <label className="flex items-center gap-2 border border-neutral-800 bg-neutral-950 px-3.5 focus-within:border-neutral-600 transition-colors">
            <Lock className="w-4 h-4 text-neutral-500" />
            <input
              data-testid="admin-password"
              type="password" required value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password" autoComplete="current-password"
              className="flex-1 bg-transparent border-0 outline-none py-3 text-sm text-white placeholder:text-neutral-600"
            />
          </label>
          {err && <div className="text-xs text-red-400" data-testid="admin-login-error">{err}</div>}
          <button
            type="submit" disabled={busy} data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-white text-black font-bold text-sm uppercase tracking-wider px-6 py-3 hover:bg-neutral-200 transition-colors disabled:opacity-50"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
        <div className="mt-4 text-xs text-center">
          <a href="/forgot-password" data-testid="admin-goto-forgot" className="text-neutral-500 hover:text-white">
            forgot password?
          </a>
        </div>
      </motion.div>
    </section>
  );
}
